import SearchInput from 'tio-ui/components/search-input';
import Checkbox from 'tio-ui/components/checkbox';
import Select from 'tio-ui/components/select';
import { Section, VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import { type IntlService } from 'ember-intl';
import Component from '@glimmer/component';
import ENV from 'tio-employee/config/environment';
import pageTitle from 'ember-page-title/helpers/page-title';
import Paginator from 'tio-ui/components/paginator';
import PageHeader from 'tio-ui/components/page-header';
import PageControlBar from 'tio-ui/components/page-control-bar';

import SearchPalette from 'tio-common/components/search/palette';
import TasApprovalQueueRow from 'tio-common/components/tas/approval/queue-row';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { SearchModelWith } from 'tio-data/data/resources/search.ext';
import type { TasProgramInstance as TasProgramInstanceResult } from 'tio-data/data/resources/search.ext';
import type AdminTasApprovalQueueController from 'tio-employee/controllers/authenticated/admin/tas/approval-queue';
import type RouterService from '@ember/routing/router-service';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type SessionContextService from 'tio-employee/services/session-context';
import type SessionService from 'tio-employee/services/session';
import type { Store as StoreService } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { Appearance } from 'tio-common/services/appearance';
import type CableService from 'tio-common/services/cable';
import type MultiDocumentAppearanceService from 'tio-common/services/multi-document-appearance';
import type { MultiDocumentAppearanceMap } from 'tio-common/services/multi-document-appearance';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';

interface S {
  Args: {
    controller: AdminTasApprovalQueueController;
    model: SearchModelWith<TasProgramInstanceResult>;
  };
}

export default class TasApprovalQueueComponent extends Component<S> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare cable: CableService;
  @service declare demoMode: DemoModeService;
  @service('multi-document-appearance')
  declare multiDocAppearance: MultiDocumentAppearanceService;

  @tracked programsDisabled = false;
  @tracked selectedProgramKeys!: string[];
  @tracked showDeleteConfirmation = false;

  @tracked presenceData: Record<string, Appearance[]> = {};

  get sortMode() {
    const order = this.args.controller.order;

    if (order.startsWith('first_submitted_')) {
      return 'first_submitted';
    }
    if (order.startsWith('last_action_')) {
      return 'last_action';
    }

    // Backward compatibility with old query-param values
    if (order.includes('attention_priority_date')) {
      return 'first_submitted';
    }

    return 'last_action';
  }

  get sortDirection() {
    const order = this.args.controller.order;

    if (order.endsWith('_asc') || order.includes('.asc') || order === 'attention_priority_date') {
      return 'asc';
    }

    return 'desc';
  }

  get sortOptions() {
    const lastAction = this.intl.t('tuition_assistance.approval_queue.sort_last_action');
    const firstSubmitted = this.intl.t('tuition_assistance.approval_queue.sort_first_submitted');
    const oldest = this.intl.t('tuition_assistance.approval_queue.oldest_first');
    const newest = this.intl.t('tuition_assistance.approval_queue.newest_first');
    return [
      { key: 'last_action_desc', label: `${lastAction}: ${newest}` },
      { key: 'last_action_asc', label: `${lastAction}: ${oldest}` },
      { key: 'first_submitted_asc', label: `${firstSubmitted}: ${oldest}` },
      { key: 'first_submitted_desc', label: `${firstSubmitted}: ${newest}` },
    ];
  }

  get currentSortOrder() {
    return `${this.sortMode}_${this.sortDirection}`;
  }

  private subscribedApplicationIds: string[] = [];
  private subscribedProgramIds: string[] = [];

  willDestroy() {
    super.willDestroy();
    if (this.subscribedApplicationIds.length) {
      this.multiDocAppearance.unsubscribe('tas_course_application', this.subscribedApplicationIds);
    }
    if (this.subscribedProgramIds.length) {
      this.multiDocAppearance.unsubscribe('tas_program_instance', this.subscribedProgramIds);
    }
  }

  // Helper to compare arrays regardless of order (for subscription change detection)
  private arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  }

  // Remove presence data for IDs that are no longer subscribed
  private removeStalePresenceData(idsToRemove: string[]) {
    const newPresenceData = { ...this.presenceData };
    for (const id of idsToRemove) {
      delete newPresenceData[id];
    }
    this.presenceData = newPresenceData;
  }

  private isAppearance(value: unknown): value is Appearance {
    return typeof value === 'object' && value !== null && 'personId' in value;
  }

  private isAppearanceRecord(value: unknown): value is Record<string, Appearance> {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return false;
    }

    return Object.values(value).every((entry) => this.isAppearance(entry));
  }

  private normalizePresenceData(data: MultiDocumentAppearanceMap): Record<string, Appearance[]> {
    const normalized: Record<string, Appearance[]> = {};

    for (const [docId, value] of Object.entries(data)) {
      if (Array.isArray(value)) {
        if (value.every((entry) => this.isAppearance(entry))) {
          normalized[docId] = value;
        } else if (value.length === 1 && this.isAppearanceRecord(value[0])) {
          normalized[docId] = Object.values(value[0]);
        } else if (value.every((entry) => this.isAppearanceRecord(entry))) {
          normalized[docId] = value.flatMap((entry) => Object.values(entry));
        } else {
          normalized[docId] = [];
        }
      } else if (this.isAppearanceRecord(value)) {
        normalized[docId] = Object.values(value);
      } else {
        normalized[docId] = [];
      }
    }

    return normalized;
  }

  // Subscribe to multi-document appearance when items change
  presenceSubscription = trackedFunction(this, async () => {
    // A demo session must not open the live ActionCable socket (the queue rows
    // are fixtures — there is no real presence to subscribe to), mirroring the
    // authenticated route's cable skip.
    if (this.demoMode.isActive) return;
    const items = this.args.model?.items;
    if (!items?.length) return;

    // Separate application IDs and program instance IDs
    const applicationIds = items
      .filter((item) => item.recordType !== 'instance')
      .map((item) => item.tasApplicationIdKeyword)
      .filter(Boolean) as string[];

    const programIds = items
      .filter(
        (item) =>
          (item.recordType === 'instance' || !item.tasApplicationIdKeyword) &&
          item.tasProgramInstanceIdKeyword
      )
      .map((item) => item.tasProgramInstanceIdKeyword)
      .filter(Boolean) as string[];

    // Cable should already be configured by the instance-initializer at app startup.
    if (!this.cable.isConfigured) {
      const accessToken = this.session.data?.authenticated?.access_token;
      if (!accessToken) {
        console.error('No access token available for cable configuration');
        return;
      }

      console.warn('Cable not configured by initializer, configuring now');
      this.cable.configure(ENV.apiHost, ENV.apiKey, accessToken, ENV.wsapiHost);
    }

    const appIdsChanged = !this.arraysEqual(applicationIds, this.subscribedApplicationIds);
    if (appIdsChanged) {
      // Clean up stale presence data from old subscriptions
      if (this.subscribedApplicationIds.length) {
        this.removeStalePresenceData(this.subscribedApplicationIds);
        this.multiDocAppearance.unsubscribe(
          'tas_course_application',
          this.subscribedApplicationIds
        );
      }
      this.subscribedApplicationIds = applicationIds;
      if (applicationIds.length) {
        this.multiDocAppearance.subscribe('tas_course_application', applicationIds, (data) => {
          const normalized = this.normalizePresenceData(data);
          this.presenceData = { ...this.presenceData, ...normalized };
        });
      }
    }

    const progIdsChanged = !this.arraysEqual(programIds, this.subscribedProgramIds);
    if (progIdsChanged) {
      // Clean up stale presence data from old subscriptions
      if (this.subscribedProgramIds.length) {
        this.removeStalePresenceData(this.subscribedProgramIds);
        this.multiDocAppearance.unsubscribe('tas_program_instance', this.subscribedProgramIds);
      }
      this.subscribedProgramIds = programIds;
      if (programIds.length) {
        this.multiDocAppearance.subscribe('tas_program_instance', programIds, (data) => {
          const normalized = this.normalizePresenceData(data);
          this.presenceData = { ...this.presenceData, ...normalized };
        });
      }
    }
  });

  // Get viewers for a specific document ID (works for both applications and programs)
  getViewersForDocument = (application: TasProgramInstanceResult) => {
    const docId =
      application.recordType === 'instance'
        ? application.tasProgramInstanceIdKeyword
        : application.tasApplicationIdKeyword || application.tasProgramInstanceIdKeyword;
    if (!docId) return [];
    return this.presenceData[docId] || [];
  };

  get currentPersonId(): string | null | undefined {
    return this.sessionContext.person?.id;
  }

  get currentCompanyId(): string | null {
    return this.sessionContext.currentEmployee.company?.id || null;
  }

  reviewStates = [
    { value: 'Course Review', key: 'courses' },
    { value: 'Program Review', key: 'program' },
    { value: 'Completion Approval', key: 'completion' },
    { value: 'Withdrawal Approval', key: 'withdrawal' },
    { value: 'Evidence Review', key: 'evidence' },
  ];

  programOptionsData = trackedFunction(this, async () => {
    if (!this.currentCompanyId) {
      return [];
    }
    const { content } = (await this.store.request(
      query<TasProgramTemplateModel>('tas-program-template', {
        'filter[company]': this.currentCompanyId as string,
      })
    )) as { content: { data: TasProgramTemplateModel[] } };
    return content.data;
  });

  get programOptions() {
    return (
      this.programOptionsData.value?.map((program) => {
        return {
          key: program.id,
          value: getSingleValueForTasField('PROGRAM_NAME', program.fields) || '',
        };
      }) || []
    );
  }

  get companyFilters() {
    const companyId = this.sessionContext.currentEmployee.company?.id;
    return companyId ? { companyId } : {};
  }

  didSearch = (string: string) => {
    this.args.controller.search = string;
  };

  changePaginatorPage = (page: number) => {
    this.args.controller.page = page;
  };

  setSelectedProgramFilter = (selectedKey: string | null) => {
    this.args.controller.program = selectedKey || '';
  };

  onStatusChange = (status: string | null) => {
    this.args.controller.approval = status || '';
  };

  onWaitingOnOthersChange = (checked: boolean) => {
    this.args.controller.waitingOnOthers = checked;
  };

  onSortChange = (key: string | null) => {
    if (!key || this.args.controller.order === key) return;
    this.args.controller.order = key;
    this.args.controller.page = 1;
  };

  <template>
    {{pageTitle (t "tuition_assistance.applications.default")}}
    <VStack>
      <PageHeader @title={{t "tuition_assistance.applications.default"}} @sticky={{false}}>
        <:toolbar>
          <SearchInput
            data-test-id="approval-search-input"
            placeholder="App ID, Employee ID, Email"
            @value={{@controller.search}}
            @onChange={{this.didSearch}}
            @onSearch={{this.didSearch}}
          />
        </:toolbar>
        <:actions>
          <SearchPalette
            @searchTypes={{hash enablePeople=false enableTas=true enablePslf=false}}
            @filters={{this.companyFilters}}
            @routeConfig={{hash
              peopleRoute="authenticated.admin.people.show"
              tasRoute="authenticated.admin.tuition-assistance.program-instances.show"
              tasApplicationRoute="authenticated.admin.tas.applications.approval"
              pslfRoute="authenticated.admin.pslf.forms"
            }}
          />
        </:actions>
        <:below>
          <PageControlBar
            @count={{@model.total}}
            @countLabel={{t "common.reporting.tas.approval.pending"}}
          >
            <:filters>
              <Select
                class="min-w-48"
                @placeholder="By program"
                @items={{this.programOptions}}
                @selected={{@controller.program}}
                @onChange={{this.setSelectedProgramFilter}}
                @clearable={{true}}
              />
              <Select
                class="min-w-48"
                @placeholder={{t "common.status"}}
                @items={{this.reviewStates}}
                @selected={{@controller.approval}}
                @onChange={{this.onStatusChange}}
                @clearable={{true}}
              />
              <Checkbox
                data-test-id="waiting-on-others-checkbox"
                @label="Waiting on others"
                @checked={{@controller.waitingOnOthers}}
                @onChange={{this.onWaitingOnOthersChange}}
              />
            </:filters>
            <:trailing>
              <Select
                class="w-72"
                @placeholder={{t "tuition_assistance.approval_queue.sort_by"}}
                @items={{this.sortOptions}}
                @selected={{this.currentSortOrder}}
                @onChange={{this.onSortChange}}
                @clearable={{false}}
              />
            </:trailing>
          </PageControlBar>
        </:below>
      </PageHeader>
      <Section class="overflow-x-auto">
        <:body>
          {{! Consume trackedFunction to trigger presence subscription }}
          {{#if this.presenceSubscription.isResolved}}{{/if}}
          <VStack @collapsed={{true}}>
            {{#if @model.items.length}}
              {{#each @model.items as |application|}}
                <TasApprovalQueueRow
                  @application={{application}}
                  @personRoute="authenticated.admin.employees.show"
                  @personModel={{application.employeeId}}
                  @rootRoute="authenticated.admin"
                  @viewers={{this.getViewersForDocument application}}
                />
              {{/each}}
            {{else}}
              <p class="text-center tio-fg-muted py-10">
                {{t "tuition_assistance.approval_queue.no_items"}}
              </p>
            {{/if}}
            <Paginator
              @totalItems={{@model.total}}
              @pageSize={{@controller.limit}}
              @currentPage={{@controller.page}}
              @onChange={{this.changePaginatorPage}}
            />
          </VStack>
        </:body>
      </Section>
    </VStack>
  </template>
}
